import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';

import { useLocation } from '../../../contexts/LocationContext';
import api from '../../../utils/apiConfig';

const FieldMarketing = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [ideas, setIdeas] = useState([]);
  const [ideaDates, setIdeaDates] = useState({}); // { ideaId: 'YYYY-MM-DD' }
  const { selectedLocation } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]); // field marketing activities (includes partnerships)
  const [campaigns, setCampaigns] = useState([]);
  const [fmPagination, setFmPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: rowsPerPage });

  // Derived collections from fetched data
  const events = useMemo(() => (activities || []).filter(a => (a?.type || '').toLowerCase() !== 'partnership'), [activities]);
  const partnerships = useMemo(() => (activities || []).filter(a => (a?.type || '').toLowerCase() === 'partnership'), [activities]);
  const localCampaigns = campaigns || [];

  // Fetch data when location/page/rows change
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedLocation?._id) return;
      setLoading(true);
      setError(null);
      try {
        // Field Marketing Activities
        const [fmRes, campRes] = await Promise.all([
          api.get('/marketing/field-marketing', {
            params: {
              locationId: selectedLocation._id,
              page: page + 1,
              limit: rowsPerPage,
            },
          }),
          api.get('/marketing/campaigns', {
            params: {
              locationId: selectedLocation._id,
              page: 1,
              limit: 50,
            },
          }),
        ]);

        const fmOk = fmRes?.data?.success && fmRes?.data?.data;
        const campOk = campRes?.data?.success && campRes?.data?.data;

        if (fmOk) {
          setActivities(fmRes.data.data.activities || []);
          setFmPagination(fmRes.data.data.pagination || { currentPage: page + 1, totalPages: 1, totalItems: 0, itemsPerPage: rowsPerPage });
        } else {
          setActivities([]);
        }

        if (campOk) {
          // campaigns may return array directly or wrapped; assume array at data.campaigns if present else data
          const camps = campRes.data.data.campaigns || campRes.data.data || [];
          setCampaigns(camps);
        } else {
          setCampaigns([]);
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load field marketing data');
        setActivities([]);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLocation?._id, page, rowsPerPage]);

  const stats = useMemo(() => {
    const activeEventCount = events.filter(e => (e?.status || '').toLowerCase() !== 'completed' && (e?.status || '').toLowerCase() !== 'cancelled').length;
    const activePartnerships = partnerships.filter(p => {
      const s = (p?.status || '').toLowerCase();
      return s === 'planned' || s === 'in_progress' || s === 'active';
    }).length;
    const partnerRevenue = partnerships.reduce((acc, p) => acc + (Number(p?.metrics?.revenue) || 0), 0);
    const campaignBudget = localCampaigns.reduce((acc, c) => acc + (Number(c?.budget) || Number(c?.budget?.total) || 0), 0);
    return [
      {
        title: 'Active Events',
        value: activeEventCount.toString(),
        icon: <EventIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      },
      {
        title: 'Active Partnerships',
        value: activePartnerships.toString(),
        icon: <BusinessIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      },
      {
        title: 'Partner Revenue',
        value: `$${partnerRevenue.toLocaleString()}`,
        icon: <MoneyIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      },
      {
        title: 'Campaign Budget',
        value: `$${campaignBudget.toLocaleString()}`,
        icon: <CampaignIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      },
    ];
  }, [events, partnerships, localCampaigns]);

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'Active':
      case 'active':
      case 'confirmed':
        return 'success';
      case 'scheduled':
      case 'planned':
        return 'info';
      case 'pending':
      case 'draft':
      case 'in_progress':
        return 'warning';
      case 'completed':
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleDetailsOpen = (item) => {
    setSelectedItem(item);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const scheduleToCalendar = (item, overrideDate) => {
    if (!item) return;
    // Map Field Marketing item to calendar event draft
    const title = item.name || item.partnerName || item.title || 'Field Marketing Activity';
    const type = 'Campaign';
    const date = overrideDate || item.date || item.startDate || new Date().toISOString().split('T')[0];
    const time = item.time || '09:00';
    const description = item.goals || item.notes || item.description || `${title} scheduled from Field Marketing`;
    const audience = item.targetAudience || '';
    const channel = 'Field';

    const draft = { title, type, date, time, description, audience, channel };
    try {
      localStorage.setItem('calendarDraftEvent', JSON.stringify(draft));
    } catch (_) {}
    navigate('/dashboard/marketing/calendar');
  };

  const generateIdeas = () => {
    // Placeholder until AI endpoint is wired: generate a few suggested activities
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    const ideasGen = [
      {
        id: 'idea-1',
        title: 'Neighborhood Flyer Drop',
        category: 'Local Outreach',
        impact: 'Medium',
        effort: 'Low',
        suggestion: 'Distribute branded flyers to nearby neighborhoods with a QR code to a landing page.',
        recommendedDate: fmt(today),
      },
      {
        id: 'idea-2',
        title: 'In-Store Demo Weekend',
        category: 'Event',
        impact: 'High',
        effort: 'Medium',
        suggestion: 'Host a weekend demo showcasing the new collection; invite local partners to co-host.',
        recommendedDate: fmt(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3)),
      },
      {
        id: 'idea-3',
        title: 'Partner Co-Promo',
        category: 'Partnership',
        impact: 'High',
        effort: 'Low',
        suggestion: 'Coordinate a cross-promotion with Interior Design Studio ABC featuring bundle discounts.',
        recommendedDate: fmt(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7)),
      },
    ];
    setIdeas(ideasGen);
    const defaults = Object.fromEntries(ideasGen.map(i => [i.id, i.recommendedDate]));
    setIdeaDates(defaults);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Field Marketing
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ fontWeight: 'bold' }}
        >
          New Activity
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Events & Trade Shows" />
            <Tab label="Local Partnerships" />
            <Tab label="Local Campaigns" />
            <Tab label="AI Ideas" />
          </Tabs>

          {/* Events Tab */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {loading && <LinearProgress />}
            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            {events.map((event) => (
              <Paper key={event.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {event.title || event.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip label={event.type} size="small" />
                      <Chip
                        label={event.status}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      {event.location || event.locationName || event?.locationId?.name || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      {(event.date || event.startDate || '—')} {event.endDate ? `- ${event.endDate}` : ''}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Budget</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          ${Number(event?.budget?.total ?? event?.budget ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Estimated Leads</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {Number(event?.expectedOutcome?.estimatedLeads ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Audience</Typography>
                        <Typography variant="body1">{event?.expectedOutcome?.targetAudience ?? '—'}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, event)}
                      size="small"
                    >
                      <MoreIcon />
                    </IconButton>
                    <Button size="small" sx={{ ml: 1 }} onClick={() => scheduleToCalendar(event)}>
                      Schedule on Calendar
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
            {!loading && events.length === 0 && (
              <Typography variant="body2" color="text.secondary">No events found for this location.</Typography>
            )}
          </TabPanel>

          {/* Partnerships Tab */}
          <TabPanel value={currentTab} index={1}>
            {loading && <LinearProgress />}
            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            {partnerships.map((partner) => (
              <Paper key={partner.id || partner._id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {partner.title || partner.partnerName || partner.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {partner.partnerType && <Chip label={partner.partnerType} size="small" />}
                      {partner.partnershipType && <Chip label={partner.partnershipType} size="small" variant="outlined" />}
                      <Chip
                        label={partner.status}
                        color={getStatusColor(partner.status)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {partner.contactPerson ? `Contact: ${partner.contactPerson}` : null}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {(partner.email || '—')} • {(partner.phone || '—')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Revenue</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          ${Number(partner?.metrics?.revenue ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Referrals</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {Number(partner?.metrics?.leadsCollected ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Commission</Typography>
                        <Typography variant="body1">{partner.commissionRate || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Since</Typography>
                        <Typography variant="body1">{partner.startDate || '—'}</Typography>
                      </Grid>
                    </Grid>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <strong>Notes:</strong> {partner.notes || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, partner)}
                      size="small"
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
            {!loading && partnerships.length === 0 && (
              <Typography variant="body2" color="text.secondary">No partnerships found for this location.</Typography>
            )}
          </TabPanel>

          {/* Local Campaigns Tab */}
          <TabPanel value={currentTab} index={2}>
            {loading && <LinearProgress />}
            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            {localCampaigns.map((campaign) => (
              <Paper key={campaign.id || campaign._id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {campaign.name || campaign.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {campaign.type && <Chip label={campaign.type} size="small" />}
                      <Chip
                        label={campaign.status}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {campaign.channels ? `Channels: ${campaign.channels.join(', ')}` : null}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Duration: {(campaign.startDate || '—')} - {(campaign.endDate || '—')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">Budget</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          ${Number(campaign?.budget ?? campaign?.budget?.total ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">Expected Reach</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {Number(campaign?.expectedReach ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">Target Audience</Typography>
                        <Typography variant="body1">{campaign.targetAudience || '—'}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, campaign)}
                      size="small"
                    >
                      <MoreIcon />
                    </IconButton>
                    <Button size="small" sx={{ ml: 1 }} onClick={() => scheduleToCalendar(campaign)}>
                      Schedule on Calendar
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </TabPanel>

          {/* AI Ideas Tab */}
          <TabPanel value={currentTab} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>AI-Generated Field Marketing Ideas</Typography>
              <Button variant="contained" onClick={generateIdeas}>Generate Ideas</Button>
            </Box>

            {ideas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Click "Generate Ideas" to get suggested activities based on personas and campaigns. Then schedule to the Marketing Calendar.
              </Typography>
            ) : (
              ideas.map((idea) => (
                <Paper key={idea.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ mr: 2, flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{idea.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {idea.category} • Impact: {idea.impact} • Effort: {idea.effort}
                      </Typography>
                      <Typography variant="body2">{idea.suggestion}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        type="date"
                        size="small"
                        label="Date"
                        InputLabelProps={{ shrink: true }}
                        value={ideaDates[idea.id] || ''}
                        onChange={(e) => setIdeaDates({ ...ideaDates, [idea.id]: e.target.value })}
                      />
                      <Button variant="outlined" onClick={() => scheduleToCalendar({ title: idea.title, description: idea.suggestion }, ideaDates[idea.id])}>
                        Schedule
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDetailsOpen(selectedItem)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => { scheduleToCalendar(selectedItem); handleMenuClose(); }}>
          <ScheduleIcon sx={{ mr: 1 }} />
          Schedule on Calendar
        </MenuItem>
        <MenuItem>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem?.name || selectedItem?.partnerName} - Details
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box>
              {/* Event Details */}
              {selectedItem.type && selectedItem.location && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Event Information
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Type:</strong> {selectedItem.type}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Location:</strong> {selectedItem.location}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Date:</strong> {selectedItem.date} - {selectedItem.endDate}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Time:</strong> {selectedItem.time}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Goals:</strong> {selectedItem.goals}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Event Contacts
                    </Typography>
                    <List>
                      {selectedItem.contacts?.map((contact, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={contact.name}
                            secondary={`${contact.phone} • ${contact.email}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              )}

              {/* Partnership Details */}
              {selectedItem.partnerName && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Partnership Details
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Partner Type:</strong> {selectedItem.partnerType}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Partnership Type:</strong> {selectedItem.partnershipType}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Contact:</strong> {selectedItem.contactPerson}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {selectedItem.email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {selectedItem.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Recent Activity
                    </Typography>
                    <List>
                      {selectedItem.recentActivity?.map((activity, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={activity.activity}
                            secondary={activity.date}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit</Button>
        </DialogActions>
      </Dialog>

      {/* Create Activity Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Field Marketing Activity</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Activity Type</InputLabel>
            <Select label="Activity Type">
              <MenuItem value="event">Event/Trade Show</MenuItem>
              <MenuItem value="partnership">Partnership</MenuItem>
              <MenuItem value="campaign">Local Campaign</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Name/Title"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Budget"
            margin="normal"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Activity</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldMarketing;
